<template>
  <v-card v-bind="$attrs" class="ncrm-contract-item-fields-card" variant="flat">
    <v-card-text class="ncrm-contract-item-fields-card__body pa-4">
      <div class="d-flex align-start justify-space-between flex-wrap ga-3 mb-2">
        <div class="ncrm-contract-item-fields-card__summary">
          <div v-if="showItemSummary" class="text-subtitle-2 ncrm-text-strong">
            {{ itemTitle }}
          </div>
          <div v-if="showItemSummary" class="text-body-2 text-medium-emphasis">
            {{ itemSubtitle }}
          </div>
          <div v-else class="text-subtitle-2 ncrm-text-strong">
            {{ title || $t("ncrm.contracts.itemFields.title") }}
          </div>
        </div>

        <v-btn
          v-if="allowAdd"
          size="small"
          variant="text"
          color="primary"
          @click="openCreateFieldDialog"
        >
          <v-icon start size="18">mdi-plus</v-icon>
          {{ $t("ncrm.contracts.itemFields.addAction") }}
        </v-btn>
      </div>

      <v-alert
        v-if="!fieldSections.length"
        type="info"
        density="comfortable"
      >
        {{ $t("ncrm.contracts.itemFields.empty") }}
      </v-alert>

      <div v-else class="ncrm-contract-item-fields-card__sections">
        <div v-if="mergeSections" class="ncrm-contract-item-fields-card__field-grid">
          <NcrmContractItemFieldEditor
            v-for="field in visibleFieldItems"
            :key="`contract-item-field-${field.id}`"
            v-model="fieldDrafts[field.id]"
            :field="field"
            :select-options="fieldSelectOptions(field)"
            :select-menu-props="selectMenuProps"
            :loading="isFieldSaving(field.id)"
            :has-changes="hasFieldDraftChanges(field)"
            :manual="isManualField(field)"
            :wide="isWideField(field)"
            :preview-values="previewValues"
            :show-field-meta="showFieldMeta"
            :editing="isFieldEditing(field.id)"
            @edit-value="startFieldEditing(field)"
            @save="saveFieldValue(field)"
            @reset="resetFieldDraft(field)"
          />
        </div>

        <section
          v-else
          v-for="section in fieldSections"
          :key="section.key"
          class="ncrm-contract-item-fields-card__section"
        >
          <div class="ncrm-contract-item-fields-card__section-header mb-2">
            <div class="d-flex align-center flex-wrap ga-2">
              <div class="text-caption text-medium-emphasis">
                {{ section.label }}
              </div>
              <v-chip
                v-if="isCurrentStageSection(section)"
                size="x-small"
                color="primary"
              >
                {{ $t("ncrm.contracts.itemFields.currentStageBadge") }}
              </v-chip>
            </div>
          </div>

          <div class="ncrm-contract-item-fields-card__field-grid">
            <NcrmContractItemFieldEditor
              v-for="field in section.fields"
              :key="`contract-item-field-${field.id}`"
              v-model="fieldDrafts[field.id]"
              :field="field"
              :select-options="fieldSelectOptions(field)"
              :select-menu-props="selectMenuProps"
              :loading="isFieldSaving(field.id)"
              :has-changes="hasFieldDraftChanges(field)"
              :manual="isManualField(field)"
              :wide="isWideField(field)"
              :preview-values="previewValues"
              :show-field-meta="showFieldMeta"
              :editing="isFieldEditing(field.id)"
              @edit-value="startFieldEditing(field)"
              @save="saveFieldValue(field)"
              @reset="resetFieldDraft(field)"
            />
          </div>
        </section>
      </div>
    </v-card-text>
  </v-card>

  <NcrmContractItemFieldDialog
    v-model="fieldDialog"
    :mode="fieldDialogMode"
    :loading="fieldDialogLoading"
    :can-submit="canSubmitFieldDialog"
    :form="fieldForm"
    :field-type-options="fieldTypeOptions"
    :select-menu-props="selectMenuProps"
    @submit="submitFieldDialog"
  />
</template>

<script>
import axios from "axios";
import NcrmContractItemFieldDialog from "./NcrmContractItemFieldDialog.vue";
import NcrmContractItemFieldEditor from "./NcrmContractItemFieldEditor.vue";
import {
  parsePositiveId,
  FIELD_TYPE_META,
  CONTRACT_ITEM_FIELD_TYPE_ORDER,
  buildEmptyFieldForm,
} from "./contractItemFieldsShared.mjs";
import { formatDisplayAmount, formatDisplayNumber } from "@/Utile/displayFormat.mjs";

export default {
  name: "NcrmContractItemFieldsCard",
  // Template has two root nodes (card + teleported dialog), so fallthrough
  // attributes (e.g. class) can't auto-inherit; route them to the card root.
  inheritAttrs: false,
  components: {
    NcrmContractItemFieldDialog,
    NcrmContractItemFieldEditor,
  },
  props: {
    contractId: {
      type: [String, Number],
      required: true,
    },
    contractItem: {
      type: Object,
      required: true,
    },
    allowAdd: {
      type: Boolean,
      default: true,
    },
    showItemSummary: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: "",
    },
    currentStageId: {
      type: [String, Number],
      default: null,
    },
    previewValues: {
      type: Boolean,
      default: false,
    },
    showFieldMeta: {
      type: Boolean,
      default: true,
    },
    mergeSections: {
      type: Boolean,
      default: false,
    },
    visibleStageIds: {
      type: Array,
      default: null,
    },
  },
  emits: ["updated", "notify"],
  data() {
    return {
      fieldDialog: false,
      fieldDialogMode: "create",
      fieldDialogLoading: false,
      fieldForm: buildEmptyFieldForm(),
      fieldDrafts: {},
      editingFieldIds: [],
      savingFieldIds: [],
      selectMenuProps: {
        maxHeight: 320,
        contentClass: "ncrm-contract-item-fields-card__select-menu",
        zIndex: 2710,
      },
    };
  },
  computed: {
    normalizedContractId() {
      return parsePositiveId(this.contractId);
    },
    normalizedContractItemId() {
      return parsePositiveId(this.contractItem?.id);
    },
    normalizedCurrentStageId() {
      return parsePositiveId(this.currentStageId);
    },
    normalizedVisibleStageIdSet() {
      if (!Array.isArray(this.visibleStageIds)) return null;
      const ids = this.visibleStageIds.map((value) => parsePositiveId(value)).filter(Boolean);
      return new Set(ids);
    },
    itemTitle() {
      return (
        String(this.contractItem?.product_name_snapshot || this.contractItem?.product_name || "").trim()
        || `#${this.normalizedContractItemId || "-"}`
      );
    },
    itemSubtitle() {
      const parts = [];
      const quantity = Number(this.contractItem?.quantity);
      if (Number.isFinite(quantity)) {
        parts.push(this.$t("ncrm.contracts.itemFields.itemQuantity", { value: this.formatNumber(quantity) }));
      }
      const billing = String(this.contractItem?.billing_frequency || this.contractItem?.billing_type || "").trim();
      if (billing) {
        parts.push(this.$t(`ncrm.contracts.billingFrequency.${billing.toLowerCase()}`));
      }
      const total = Number(this.contractItem?.total_amount);
      if (Number.isFinite(total)) {
        parts.push(this.$t("ncrm.contracts.itemFields.itemTotal", { value: this.formatMoney(total) }));
      }
      return parts.filter(Boolean).join(" · ");
    },
    fieldItems() {
      return Array.isArray(this.contractItem?.fields) ? this.contractItem.fields : [];
    },
    visibleFieldItems() {
      return this.fieldItems.filter((field) => {
        const stageId = parsePositiveId(field?.template_stage_id);
        if (!stageId || !this.normalizedVisibleStageIdSet) return true;
        return this.normalizedVisibleStageIdSet.has(stageId);
      });
    },
    fieldSections() {
      const sections = [];
      const sectionMap = new Map();
      for (const field of this.visibleFieldItems) {
        const stageId = parsePositiveId(field?.template_stage_id);
        const key = stageId ? `stage:${stageId}` : "general";
        if (!sectionMap.has(key)) {
          const section = {
            key,
            stageId,
            label: stageId
              ? String(field?.stage_name || "").trim() || this.$t("ncrm.contracts.itemFields.stageFallback")
              : this.$t("ncrm.contracts.itemFields.generalSection"),
            fields: [],
          };
          sectionMap.set(key, section);
          sections.push(section);
        }
        sectionMap.get(key).fields.push(field);
      }
      return sections;
    },
    fieldTypeOptions() {
      return CONTRACT_ITEM_FIELD_TYPE_ORDER.map((value) => ({
        value,
        label: this.fieldTypeLabel(value),
        icon: this.fieldTypeIcon(value),
      }));
    },
    canSubmitFieldDialog() {
      if (!String(this.fieldForm.label || "").trim()) return false;
      if (!String(this.fieldForm.field_type || "").trim()) return false;
      if (this.fieldForm.field_type === "select" && !this.normalizedOptionLabels(this.fieldForm.option_labels).length) {
        return false;
      }
      return !this.fieldDialogLoading;
    },
  },
  watch: {
    fieldItems: {
      immediate: true,
      deep: true,
      handler() {
        this.syncFieldDrafts();
      },
    },
  },
  methods: {
    formatNumber(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? formatDisplayNumber(parsed) : "0";
    },
    formatMoney(value) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return "-";
      return `${formatDisplayAmount(parsed)} ${String(this.contractItem?.currency || "MDL").toUpperCase()}`;
    },
    fieldTypeLabel(type) {
      const normalized = String(type || "").trim().toLowerCase();
      const key = `ncrm.contracts.itemFields.types.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    fieldTypeIcon(type) {
      return FIELD_TYPE_META[String(type || "").trim().toLowerCase()]?.icon || "mdi-form-textbox";
    },
    isManualField(field) {
      return String(field?.source_type || "").trim().toLowerCase() === "manual";
    },
    isWideField(field) {
      const normalizedType = String(field?.field_type || "").trim().toLowerCase();
      return normalizedType === "long_text";
    },
    isCurrentStageSection(section) {
      return Boolean(section?.stageId && this.normalizedCurrentStageId && section.stageId === this.normalizedCurrentStageId);
    },
    normalizedFieldValue(field) {
      if (field?.field_type === "checkbox") {
        return field?.value === true;
      }
      if (field?.field_type === "number") {
        return field?.value ?? "";
      }
      return field?.value ?? "";
    },
    normalizedOptionLabels(values) {
      return (Array.isArray(values) ? values : [])
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
    },
    syncFieldDrafts() {
      const nextDrafts = {};
      for (const field of this.fieldItems) {
        const fieldId = parsePositiveId(field?.id);
        if (!fieldId) continue;
        nextDrafts[fieldId] = this.normalizedFieldValue(field);
      }
      this.fieldDrafts = nextDrafts;
      this.editingFieldIds = this.editingFieldIds.filter((fieldId) => Object.prototype.hasOwnProperty.call(nextDrafts, fieldId));
    },
    comparableFieldValue(field, value) {
      if (field?.field_type === "checkbox") {
        return value === true ? "1" : value === false ? "0" : "";
      }
      if (field?.field_type === "number") {
        return value === null || value === undefined || value === "" ? "" : String(Number(value));
      }
      return String(value ?? "").trim();
    },
    hasFieldDraftChanges(field) {
      const fieldId = parsePositiveId(field?.id);
      if (!fieldId) return false;
      return this.comparableFieldValue(field, this.fieldDrafts[fieldId])
        !== this.comparableFieldValue(field, this.normalizedFieldValue(field));
    },
    resetFieldDraft(field) {
      const fieldId = parsePositiveId(field?.id);
      if (!fieldId) return;
      this.fieldDrafts = {
        ...this.fieldDrafts,
        [fieldId]: this.normalizedFieldValue(field),
      };
      if (this.previewValues) {
        this.editingFieldIds = this.editingFieldIds.filter((id) => id !== fieldId);
      }
    },
    isFieldEditing(fieldId) {
      const normalizedFieldId = parsePositiveId(fieldId);
      return normalizedFieldId ? this.editingFieldIds.includes(normalizedFieldId) : false;
    },
    startFieldEditing(field) {
      const fieldId = parsePositiveId(field?.id);
      if (!fieldId || this.isFieldEditing(fieldId)) return;
      this.fieldDrafts = {
        ...this.fieldDrafts,
        [fieldId]: this.normalizedFieldValue(field),
      };
      this.editingFieldIds = [...this.editingFieldIds, fieldId];
    },
    isFieldSaving(fieldId) {
      const normalizedFieldId = parsePositiveId(fieldId);
      return normalizedFieldId ? this.savingFieldIds.includes(normalizedFieldId) : false;
    },
    fieldSelectOptions(field) {
      const options = Array.isArray(field?.options) ? field.options : [];
      return options.map((option) => ({
        value: String(option?.key || "").trim(),
        label: String(option?.label || "").trim() || String(option?.key || "").trim(),
      }));
    },
    buildFieldDialogPayload() {
      const payload = {
        label: String(this.fieldForm.label || "").trim(),
        field_type: String(this.fieldForm.field_type || "url").trim().toLowerCase(),
        help_text: String(this.fieldForm.help_text || "").trim() || null,
        is_required: this.fieldForm.is_required !== false,
        sort_order: Number(this.fieldForm.sort_order) || 1,
      };
      if (payload.field_type === "select") {
        payload.options = this.normalizedOptionLabels(this.fieldForm.option_labels).map((label, index) => ({
          label,
          sort_order: index + 1,
          is_active: true,
        }));
      }
      if (payload.field_type === "checkbox") {
        payload.value = this.fieldForm.value === true;
      } else {
        payload.value = this.fieldForm.value === "" ? null : this.fieldForm.value;
      }
      return payload;
    },
    openCreateFieldDialog() {
      const nextSortOrder = this.fieldItems.length + 1;
      this.fieldDialogMode = "create";
      this.fieldForm = buildEmptyFieldForm(nextSortOrder);
      this.fieldDialog = true;
    },
    openEditFieldDialog(field) {
      this.fieldDialogMode = "edit";
      this.fieldForm = {
        id: parsePositiveId(field?.id),
        label: String(field?.label || ""),
        field_type: String(field?.field_type || "url"),
        help_text: String(field?.help_text || ""),
        is_required: Number(field?.is_required) === 1 || field?.is_required === true,
        sort_order: Number(field?.sort_order) || 1,
        save_as_template: false,
        option_labels: this.fieldSelectOptions(field).map((option) => option.label),
        value: this.normalizedFieldValue(field),
      };
      this.fieldDialog = true;
    },
    async submitFieldDialog() {
      if (!this.normalizedContractId || !this.normalizedContractItemId || !this.canSubmitFieldDialog) return;
      this.fieldDialogLoading = true;
      try {
        if (this.fieldDialogMode === "edit" && this.fieldForm.id) {
          const payload = this.buildFieldDialogPayload();
          await axios.patch(`/ncrm/contracts/${this.normalizedContractId}/item-fields/${this.fieldForm.id}`, payload);
          this.$emit("notify", {
            message: this.$t("ncrm.contracts.itemFields.messages.updated"),
            color: "success",
          });
        } else {
          const payload = {
            contract_item_id: this.normalizedContractItemId,
            save_as_template: this.fieldForm.save_as_template === true,
            ...this.buildFieldDialogPayload(),
          };
          await axios.post(`/ncrm/contracts/${this.normalizedContractId}/item-fields`, payload);
          this.$emit("notify", {
            message: this.$t("ncrm.contracts.itemFields.messages.created"),
            color: "success",
          });
        }
        this.fieldDialog = false;
        this.$emit("updated");
      } catch (error) {
        console.error("[ncrm-contract-item-fields-card] submit failed", error);
        this.$emit("notify", {
          message: error?.response?.data?.error || this.$t("ncrm.contracts.itemFields.messages.saveError"),
          color: "error",
        });
      } finally {
        this.fieldDialogLoading = false;
      }
    },
    async saveFieldValue(field) {
      if (!this.normalizedContractId) return;
      const fieldId = parsePositiveId(field?.id);
      if (!fieldId || !this.hasFieldDraftChanges(field)) return;

      this.savingFieldIds = [...this.savingFieldIds, fieldId];
      try {
        const draftValue = this.fieldDrafts[fieldId];
        const payload = {
          value: field?.field_type === "checkbox"
            ? draftValue === true
            : draftValue === "" ? null : draftValue,
        };
        await axios.patch(`/ncrm/contracts/${this.normalizedContractId}/item-fields/${fieldId}`, payload);
        this.$emit("notify", {
          message: this.$t("ncrm.contracts.itemFields.messages.valueSaved"),
          color: "success",
        });
        if (this.previewValues) {
          this.editingFieldIds = this.editingFieldIds.filter((id) => id !== fieldId);
        }
        this.$emit("updated");
      } catch (error) {
        console.error("[ncrm-contract-item-fields-card] save value failed", error);
        this.$emit("notify", {
          message: error?.response?.data?.error || this.$t("ncrm.contracts.itemFields.messages.valueSaveError"),
          color: "error",
        });
      } finally {
        this.savingFieldIds = this.savingFieldIds.filter((id) => id !== fieldId);
      }
    },
  },
};
</script>

<style scoped>
.ncrm-contract-item-fields-card {
  border: 1px solid var(--ncrm-color-border-default, rgba(var(--v-theme-on-surface), 0.1)) !important;
  border-radius: var(--app-radius-base, 12px) !important;
}

.ncrm-contract-item-fields-card__summary {
  min-width: 0;
}

.ncrm-contract-item-fields-card__body {
  padding: 14px !important;
}

.ncrm-contract-item-fields-card__sections {
  display: grid;
  gap: 14px;
}

.ncrm-contract-item-fields-card__section + .ncrm-contract-item-fields-card__section {
  padding-top: 12px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.ncrm-contract-item-fields-card__section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ncrm-contract-item-fields-card__field-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  padding: 0 12px;
  border-radius: 16px;
  border: 1px solid var(--ncrm-color-border-default);
  background: var(--ncrm-color-bg-card);
}

.ncrm-contract-item-fields-card__field-grid :deep(.ncrm-contract-item-field-editor + .ncrm-contract-item-field-editor) {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

@media (max-width: 959px) {
  .ncrm-contract-item-fields-card__body {
    padding: 12px !important;
  }

  .ncrm-contract-item-fields-card__field-grid {
    padding: 0 10px;
  }
}
</style>
